import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DynamicIdService } from 'shared/_services/dynamic.id.service';
import { ControlTemplate } from '../_interface/control-template.enum';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateCustomAdapter, NgbDateCustomParserFormatter } from 'shared/_services/ngb-date-adapter.service';
import { DEFAULT_DATE_FORMAT, INVALID_DATE_FORMAT } from 'shared/magic';
import dayjs from 'dayjs';
import { isControlRequired } from '../functions/check-if-control-required.fn';
import { InlineAlign } from '../_interface/inline-align.enum';
import { YesNo } from '../../../../journal/src/_interface/yes-no.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { adminOn, adminOff } from 'app/_actions/user.actions';
import {FormFieldWarnings} from '../../../../journal/src/_interface/form-field-warnings.interface';

@UntilDestroy()
@Component({
	selector: 'form-date-input',
	templateUrl: './date-input.component.html',
	styleUrls: ['./date-input.component.scss'],
	providers: [
		{ provide: NgbDateAdapter, useClass: NgbDateCustomAdapter },
		{ provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter }
	]
})
export class DateInputComponent implements OnInit {

	private initialValue: { entryChangeDate: string; entryChangeTime?: string };

	@ViewChild('inlinePopover', { static: false }) inlinePopover: NgbPopover;
	@ViewChild('dateAndTimePopover', { static: false }) dateAndTimePopover: NgbPopover;

	@Input()
	control: UntypedFormControl | UntypedFormGroup;

	@Input()
	placeholder: string;

	@Input()
	id: string = this.dynamicIdService.getId('date-input');

	@Input()
	label: string;

	@Input()
	clearable = false;

	@Input()
	defaultValue: string;

	@Input()
	template: ControlTemplate = ControlTemplate.inline;

	@Input()
	humanize = false;

	@Input()
	dateFormat = DEFAULT_DATE_FORMAT;

	@Input() set setWarnings(warnings: FormFieldWarnings) {
		this.warnings = warnings;
	}

	@Input() inlineAlign: InlineAlign = InlineAlign.center;

	@Input() canBeEmpty: YesNo = YesNo.yes;

	dateString: string;
	datePlaceholder = 'MM/DD/YYYY';

	ControlTemplate = ControlTemplate;
	invalidFormatDate = false;
	isControlRequired: boolean;
	warnings: FormFieldWarnings;

	touched = false;
	isFormGroup = false;
	maxDate: NgbDateStruct = this.getTodayAsStruct();
	minDate: NgbDateStruct = { year: 1900, month: 1, day: 1 };

	constructor(
		private dynamicIdService: DynamicIdService,
		private store: Store
	) { }
	
	private markAsTouchedDirty() {
		const { date, time } = this.formGroupControls;
		date.markAsTouched();
		date.markAsDirty();
		time.markAsTouched();
		time.markAsDirty();
	}

	private getTodayAsStruct(): NgbDateStruct {
		const today = dayjs();
		return {
			year: today.year(),
			month: today.month() + 1,
			day: today.date()
		};
	}

	private updateDateStringFromFormGroup() {
		const { date, time } = this.formGroupControls;
		const dateVal = date.value;
		const timeVal = time.value;
		if (!dateVal || !timeVal || typeof timeVal !== 'string' || !timeVal.includes(':')) return;
		const [hour, minute] = timeVal.split(':').map(Number);
		const base = dayjs(dateVal);
		if (!base.isValid()) return;
		const combined = base
			.set('hour', hour)
			.set('minute', minute)
			.set('second', 0)
			.set('millisecond', 0)
			.toISOString();
		this.dateString = combined;
	}

	setDateFormControl() {
		const { value } = this.control;
		this.dateString = value;
	}

	ngOnInit(): void {
		if (!this.control) throw new Error('Control is required');
		if (this.template === ControlTemplate.dateAndTime && this.control instanceof UntypedFormGroup) {
			this.isFormGroup = true;
			const { date, time } = this.formGroupControls;
			if (this.defaultValue && !date.value) {
				date.setValue(this.defaultValue, { emitEvent: false });
			}
			if (!time.value) {
				time.setValue(dayjs().format('HH:mm'), { emitEvent: false });
			}
			this.initialValue = {
				entryChangeDate: date.value,
				entryChangeTime: time.value
			};
			this.updateDateStringFromFormGroup();
			date.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
				this.markAsTouchedDirty();
				this.updateDateStringFromFormGroup();
			});
			time.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
				this.markAsTouchedDirty();
				this.updateDateStringFromFormGroup();
			});
			const now = dayjs();
			this.datePlaceholder = `${now.toISOString()}`;
		} else {
			if (this.defaultValue && this.control.value === null) this.control.setValue(this.defaultValue, { emitEvent: false });
			if (this.control.value) this.setDateFormControl();
			this.control.valueChanges.pipe(
				untilDestroyed(this)
			).subscribe((value) => {
				this.control.markAsTouched();
				this.control.markAsDirty();
				this.setDateFormControl();
				if (!value) this.clear();
			});
			this.datePlaceholder = new Date().toISOString();
		}
		this.checkIsRequired();
		this.isControlRequired = isControlRequired(this.control);
	}

	get formGroupControls() {
		const form = this.control as UntypedFormGroup;
		const date = form.get('entryChangeDate');
		const time = form.get('entryChangeTime');
		if (!date || !time) {
			return null;
		}
		return {
			date: date as UntypedFormControl,
			time: time as UntypedFormControl
		};
	}

	checkIsRequired() {
		if (this.canBeEmpty === YesNo.yes) return;
		this.control.setValidators(Validators.required);
		this.control.updateValueAndValidity({ emitEvent: false });
	}

	dateChange($event: string | null) {
		if (!this.template || !this.control) return;
		if (this.template === ControlTemplate.dateAndTime && this.isFormGroup) {
			if (!$event) return;
			const d = dayjs($event);
			const { date, time } = this.formGroupControls;
			const currentTime = time.value || '00:00';
			const [hour, minute] = currentTime.split(':').map(Number);
			const final = d.set('hour', hour).set('minute', minute).toISOString();
			date.patchValue(final);
			this.updateDateStringFromFormGroup();
			this.markUserChanged();
		} else {
			this.dateString = $event;
			this.control.patchValue(this.dateString);
			this.markUserChanged();
		}
		this.control.updateValueAndValidity();
		this.invalidFormatDate = false;
	}

	clear = () => {
		if (this.isFormGroup) {
			const { date, time } = this.formGroupControls;
			date.patchValue('', { emitEvent: false });
			time.patchValue('', { emitEvent: false });
		} else {
			this.control.patchValue('', { emitEvent: false });
		}
		this.dateString = null;
	}

	reset = () => {
		if (this.template === ControlTemplate.dateAndTime && this.isFormGroup) {
			const { entryChangeDate, entryChangeTime = '00:00' } = this.initialValue || {};
			const [hour, minute] = entryChangeTime.split(':').map(Number);
			const d = dayjs(entryChangeDate).set('hour', hour).set('minute', minute).set('second', 0).set('millisecond', 0);
			if (!d.isValid()) return;
			const { date, time } = this.formGroupControls;
			date.patchValue(d.toISOString());
			time.patchValue(entryChangeTime);
			date.markAsDirty();
			time.markAsDirty();
			this.updateDateStringFromFormGroup();
		} else {
			this.dateString = this.defaultValue;
			this.control.patchValue(this.dateString);
			this.control.markAsDirty();
			this.control.markAsTouched();
		}
	}

	dateSelect = () => {
		this.control.markAsTouched();
		this.control.markAsDirty();
	}

	onInputChange($event: any): void {
		const value = $event.target.value;
		const date = new Date(value);
		const dateValid = dayjs(date).isValid();
		this.invalidFormatDate = !(value.trim() === '') && !dateValid;
		this.hollyMollyAdmin(value);
		dateValid ? this.updateForm(date.toISOString()) : this.control.setValue('');
		if (this.invalidFormatDate) this.dateString = INVALID_DATE_FORMAT;
	}

	updateForm(newDate: string): void {
		this.dateString = newDate;
		if (this.isFormGroup) {
			const d = dayjs(newDate);
			const { date, time } = this.formGroupControls;
			date.patchValue(d.toISOString());
			time.patchValue(d.format('HH:mm'));
		} else {
			this.control.patchValue(newDate);
		}
	}

	private markUserChanged() {
		if (this.isFormGroup) {
			const fg = this.control as UntypedFormGroup;
			fg.markAsDirty();
			fg.markAsTouched();
			const { date, time } = this.formGroupControls || {};
			if (date) { date.markAsDirty(); date.markAsTouched(); }
			if (time) { time.markAsDirty(); time.markAsTouched(); }
		} else {
			(this.control as UntypedFormControl).markAsDirty();
			(this.control as UntypedFormControl).markAsTouched();
		}
	}

	/*
	* just for enabling Admin mode for Hamid, until Auth will be implemented
	*/
	hollyMollyAdmin(value: string) {
		switch (value) {
			case 'hollymollyon':
				return this.store.dispatch(adminOn());
			case 'hollymollyoff':
				return this.store.dispatch(adminOff());
			default:
				return;
		}
	}

	open() {
		if (this.template === ControlTemplate.dateAndTime) {
			this.dateAndTimePopover.open();
			return;
		}
		if (!this.inlinePopover) return;
		this.inlinePopover.open();
	}
}
